import { useCallback, useEffect, useRef, useState } from 'react';

import type { Popup } from 'config/types';
import { usePopups } from 'store';

const usePopup = (
  type: Popup['type'],
): { isPopupVisible: boolean; closePopup: () => void; popup: Popup | null } => {
  const timeout = useRef<number | null>(null);
  const { defaultDelay, lastPopup, popupsToShow, removePopup } = usePopups();
  const popup = popupsToShow[type];
  const lastPopupClosedAt = lastPopup[type]?.closedAt;

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const closePopup = useCallback(() => {
    removePopup(type);
  }, [removePopup, type]);

  useEffect(() => {
    if (!popup) {
      setIsPopupVisible(false);
    } else if (popup.immediate) {
      setIsPopupVisible(true);
    } else {
      const delay = popup.delay ?? defaultDelay;
      const waitTime =
        lastPopupClosedAt && delay
          ? delay - (Date.now() - lastPopupClosedAt)
          : delay;

      if (typeof waitTime === 'number' && waitTime > 0) {
        timeout.current = window.setTimeout(() => {
          setIsPopupVisible(true);
        }, waitTime);
      } else {
        setIsPopupVisible(true);
      }
    }

    return () => {
      if (timeout.current) {
        window.clearTimeout(timeout.current);
      }
      setIsPopupVisible(false);
    };
  }, [popup, defaultDelay, lastPopupClosedAt]);

  return { popup, isPopupVisible, closePopup };
};

export default usePopup;
